// UPost.com - Kindle Research A/B Test Dashboard (Admin)
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { 
  BarChart3, Users, DollarSign, TrendingUp, RefreshCw, 
  AlertCircle, CheckCircle, Loader2, Settings, Sliders
} from 'lucide-react';

export function KindleABDashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [recentEvents, setRecentEvents] = useState([]);
  const [weights, setWeights] = useState({ A: 25, B: 25, C: 25, D: 25 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [statsRes, eventsRes] = await Promise.all([
        api.request('/kindle-ab-test/admin/stats'),
        api.request('/kindle-ab-test/admin/recent-events?limit=30')
      ]);
      setStats(statsRes);
      setRecentEvents(eventsRes.events || []);
      
      // Set current weights
      if (statsRes.variants) {
        setWeights({
          A: statsRes.variants.A?.weight || 25,
          B: statsRes.variants.B?.weight || 25,
          C: statsRes.variants.C?.weight || 25,
          D: statsRes.variants.D?.weight || 25
        });
      }
    } catch (err) {
      setError('Failed to load stats: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateWeights = async () => {
    const total = weights.A + weights.B + weights.C + weights.D;
    if (total !== 100) {
      setError(`Weights must sum to 100 (current: ${total})`);
      return;
    }
    
    setSaving(true);
    setError('');
    try {
      await api.request('/kindle-ab-test/admin/weights', {
        method: 'PUT',
        body: JSON.stringify({
          variant_a: weights.A,
          variant_b: weights.B,
          variant_c: weights.C,
          variant_d: weights.D
        })
      });
      setSuccess('Weights updated successfully!');
      loadStats();
    } catch (err) {
      setError('Failed to update weights: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleResetTest = async () => {
    if (!window.confirm('Are you sure you want to reset all A/B test data? This will archive existing data and start fresh.')) return;
    
    setSaving(true);
    try {
      await api.request('/kindle-ab-test/admin/reset-test', { method: 'POST' });
      setSuccess('A/B test data has been archived and reset');
      loadStats();
    } catch (err) {
      setError('Failed to reset: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      </Layout>
    );
  }

  const variantColors = {
    A: 'bg-gray-500',
    B: 'bg-blue-500',
    C: 'bg-green-500',
    D: 'bg-purple-500'
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <BarChart3 className="w-8 h-8 text-purple-600" />
                Kindle Research A/B Test Dashboard
              </h1>
              <p className="text-gray-600">Monitor and optimize conversion rates</p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={loadStats}>
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
              </Button>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              {error}
              <button onClick={() => setError('')} className="ml-auto">×</button>
            </div>
          )}
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              {success}
              <button onClick={() => setSuccess('')} className="ml-auto">×</button>
            </div>
          )}

          {/* Overview Stats */}
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-3xl font-bold">{stats?.overall?.total_visitors || 0}</div>
                    <div className="text-gray-600">Total Visitors</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <div className="text-3xl font-bold">{stats?.overall?.total_setup_conversions || 0}</div>
                    <div className="text-gray-600">Setup Conversions</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <div className="text-3xl font-bold">${stats?.revenue?.total_revenue || 0}</div>
                    <div className="text-gray-600">Total Revenue</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <div className="text-3xl font-bold">{stats?.overall?.overall_setup_rate || 0}%</div>
                    <div className="text-gray-600">Overall Conv. Rate</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Tabs defaultValue="variants">
            <TabsList className="mb-6">
              <TabsTrigger value="variants">📊 Variant Performance</TabsTrigger>
              <TabsTrigger value="weights">⚖️ Adjust Weights</TabsTrigger>
              <TabsTrigger value="events">📜 Recent Events</TabsTrigger>
            </TabsList>

            <TabsContent value="variants">
              <div className="grid md:grid-cols-2 gap-6">
                {stats?.variants && Object.entries(stats.variants).map(([id, variant]) => (
                  <Card key={id} className="overflow-hidden">
                    <CardHeader className={`${variantColors[id]} text-white`}>
                      <CardTitle className="flex items-center justify-between">
                        <span>Variant {id}: {variant.name}</span>
                        <span className="text-sm font-normal bg-white/20 px-2 py-1 rounded">
                          {variant.weight}% traffic
                        </span>
                      </CardTitle>
                      <CardDescription className="text-white/80">
                        {variant.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="p-6">
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Total Visitors</span>
                          <span className="font-bold text-lg">{variant.total_visitors}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Setup Conversions</span>
                          <span className="font-bold text-lg text-green-600">{variant.setup_conversions}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Subscription Conversions</span>
                          <span className="font-bold text-lg text-purple-600">{variant.subscription_conversions}</span>
                        </div>
                        
                        <div className="pt-4 border-t">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm text-gray-600">Setup Conversion Rate</span>
                            <span className="font-bold">{variant.setup_conversion_rate}%</span>
                          </div>
                          <Progress value={variant.setup_conversion_rate} className="h-2" />
                        </div>
                        
                        <div>
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm text-gray-600">Subscription Rate</span>
                            <span className="font-bold">{variant.subscription_conversion_rate}%</span>
                          </div>
                          <Progress value={variant.subscription_conversion_rate} className="h-2" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Winner Indicator */}
              {stats?.variants && (
                <Card className="mt-6 bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                      🏆 Current Leader
                    </h3>
                    {(() => {
                      const variants = Object.entries(stats.variants);
                      const winner = variants.reduce((best, [id, v]) => 
                        v.setup_conversion_rate > (best?.rate || 0) ? { id, rate: v.setup_conversion_rate, name: v.name } : best
                      , null);
                      
                      if (!winner || winner.rate === 0) {
                        return <p className="text-gray-600">Not enough data yet. Keep testing!</p>;
                      }
                      
                      return (
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 ${variantColors[winner.id]} text-white rounded-full flex items-center justify-center text-xl font-bold`}>
                            {winner.id}
                          </div>
                          <div>
                            <div className="font-bold text-lg">{winner.name}</div>
                            <div className="text-gray-600">{winner.rate}% conversion rate</div>
                          </div>
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="weights">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sliders className="w-5 h-5" />
                    Adjust Traffic Distribution
                  </CardTitle>
                  <CardDescription>
                    Change how traffic is split between variants. Weights must sum to 100%.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    {['A', 'B', 'C', 'D'].map(id => (
                      <div key={id} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="font-medium flex items-center gap-2">
                            <span className={`w-4 h-4 rounded ${variantColors[id]}`}></span>
                            Variant {id}
                          </label>
                          <span className="text-sm text-gray-500">
                            {stats?.variants?.[id]?.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <Input 
                            type="number"
                            min="0"
                            max="100"
                            value={weights[id]}
                            onChange={e => setWeights({...weights, [id]: parseInt(e.target.value) || 0})}
                            className="w-24"
                          />
                          <span className="text-gray-500">%</span>
                          <input 
                            type="range"
                            min="0"
                            max="100"
                            value={weights[id]}
                            onChange={e => setWeights({...weights, [id]: parseInt(e.target.value)})}
                            className="flex-1"
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Total:</span>
                      <span className={`font-bold text-lg ${weights.A + weights.B + weights.C + weights.D === 100 ? 'text-green-600' : 'text-red-600'}`}>
                        {weights.A + weights.B + weights.C + weights.D}%
                      </span>
                    </div>
                    {weights.A + weights.B + weights.C + weights.D !== 100 && (
                      <p className="text-red-600 text-sm mt-2">Weights must sum to exactly 100%</p>
                    )}
                  </div>

                  <div className="flex gap-4">
                    <Button 
                      onClick={handleUpdateWeights}
                      disabled={saving || weights.A + weights.B + weights.C + weights.D !== 100}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Settings className="w-4 h-4 mr-2" />}
                      Save Weights
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => setWeights({ A: 25, B: 25, C: 25, D: 25 })}
                    >
                      Reset to Equal
                    </Button>
                  </div>

                  <div className="border-t pt-6 mt-6">
                    <h4 className="font-semibold text-red-600 mb-2">⚠️ Danger Zone</h4>
                    <p className="text-sm text-gray-600 mb-4">
                      Reset the A/B test to start fresh. This will archive all existing data.
                    </p>
                    <Button variant="destructive" onClick={handleResetTest} disabled={saving}>
                      Reset A/B Test Data
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="events">
              <Card>
                <CardHeader>
                  <CardTitle>Recent Conversion Events</CardTitle>
                  <CardDescription>Last 30 events across all variants</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {recentEvents.length > 0 ? (
                      recentEvents.map((event, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <span className={`w-8 h-8 ${variantColors[event.variant] || 'bg-gray-400'} text-white rounded-full flex items-center justify-center text-sm font-bold`}>
                              {event.variant || '?'}
                            </span>
                            <div>
                              <div className="font-medium capitalize">
                                {event.event_type.replace(/_/g, ' ')}
                              </div>
                              <div className="text-xs text-gray-500">
                                {event.variant_name || 'Unknown variant'}
                              </div>
                            </div>
                          </div>
                          <div className="text-sm text-gray-500">
                            {new Date(event.timestamp).toLocaleString()}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        No events recorded yet
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </Layout>
  );
}

export default KindleABDashboard;
